import { DependencyList, EffectCallback, useEffect, useRef } from 'react';

/**
 * Custom hook that runs an effect only after the initial mount, skipping the first render.
 * Similar to useEffect, but the effect callback is not executed on the initial component mount.
 *
 * @param effect - The effect callback to run when dependencies change (after initial mount)
 * @param deps - Optional dependency array. If provided, effect runs when dependencies change.
 *               If omitted, effect runs on every update after mount.
 *
 * @example
 * // Only logs when userId changes, not on initial mount
 * useDidUpdateEffect(() => {
 *   console.log('User ID changed:', userId);
 * }, [userId]);
 */
export function useDidUpdateEffect(effect: EffectCallback, deps?: DependencyList) {
  const isMountingRef = useRef(false);

  useEffect(() => {
    isMountingRef.current = true;
  }, []);

  useEffect(() => {
    if (!isMountingRef.current) {
      return effect();
    } else {
      isMountingRef.current = false;
    }
  }, deps);
}

export const useEffectAfterMount = useDidUpdateEffect;
